import { EndpointHint, LoadFailure, Skeleton } from '../../../modules/core/index.js'
import { BAD, MARINE, SEQUENTIAL } from '../charts/chartColors.js'
import { useInsightsQuarter } from '../hooks.js'
import { dayLabel } from '../labels.js'

const CHANNEL_LABELS: Readonly<Record<string, string>> = { 'web-push': 'Push', 'ses-email': 'Email', sms: 'Text', recording: 'Recorded' }

/**
 * The last quarter (plan section 6.7): what the 90-day stores hold, read live - the Clinical Bridge funnel and, for
 * an operator, what the deliveries reached by channel. The title says ninety days, and nothing older is offered.
 */
export function QuarterScreen({ showEndpoints = true }: { showEndpoints?: boolean }) {
  const quarter = useInsightsQuarter()
  if (quarter.isPending) {
    return <Skeleton lines={4} label="Loading the last quarter" />
  }
  if (quarter.isError) {
    return <LoadFailure message="The last quarter could not be loaded." onRetry={() => void quarter.refetch()} retrying={quarter.isFetching} />
  }
  const { bridge, delivery, from, to } = quarter.data
  const funnel = [
    { name: 'Opened', value: bridge.opened, color: '#08404F' },
    { name: 'Answered', value: bridge.answered, color: MARINE },
    { name: 'Filled', value: bridge.filled, color: SEQUENTIAL[2] ?? MARINE },
    { name: 'Expired', value: bridge.expired, color: BAD },
    { name: 'Cancelled', value: bridge.cancelled, color: '#C9D4DA' },
  ]
  const max = Math.max(1, ...funnel.map((step) => step.value))
  const channels = delivery ? Object.entries(delivery.byChannel).sort((a, b) => b[1].sent - a[1].sent) : []
  const maxSent = Math.max(1, ...channels.map(([, counters]) => counters.sent + counters.failed))

  return (
    <div className="flex flex-col gap-6">
      <p className="text-[11px] text-ink-mid">
        The ninety days from {dayLabel(from)} to {dayLabel(to)}, which is as long as these records are kept. {showEndpoints && <EndpointHint>GET /insights/quarter</EndpointHint>}
      </p>
      <div className="grid gap-6 lg:grid-cols-2">
        <section className="flex min-w-0 flex-col gap-3 rounded-lg border border-marine/15 bg-white px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="flex-1 text-sm font-black text-ink">Clinical Bridge callouts</h2>
            <span className="text-[11px] text-ink-mid">{bridge.medianMinutesToFill === null ? 'None filled yet' : `Median ${bridge.medianMinutesToFill} minutes to fill`}</span>
          </div>
          {bridge.opened === 0 ? (
            <div className="py-6 text-center text-xs text-ink-mid">No callout was opened in the last ninety days.</div>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {funnel.map((step) => (
                <li key={step.name} className="flex items-center gap-3 text-[11px]">
                  <span className="w-20 text-ink">{step.name}</span>
                  <span className="relative h-5 flex-1" role="img" aria-label={`${step.name}: ${step.value}`}>
                    <span className="absolute inset-y-0 left-0 rounded" style={{ width: `${Math.max(2, (step.value / max) * 100)}%`, background: step.color }} />
                  </span>
                  <span className="w-10 text-right font-bold text-ink">{step.value}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[10px] text-ink-mid">{bridge.opened > 0 ? `${Math.round((bridge.filled / bridge.opened) * 100)}% filled` : ''}</p>
        </section>
        <section className="flex min-w-0 flex-col gap-3 rounded-lg border border-marine/15 bg-white px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="flex-1 text-sm font-black text-ink">Reach, by channel</h2>
            <span className="text-[11px] text-ink-mid">Deliveries sent and failed</span>
          </div>
          {delivery === null ? (
            <div className="py-6 text-center text-xs text-ink-mid">The deliveries are the client's as a whole, with no split by facility, so they are the operator's to see.</div>
          ) : channels.length === 0 ? (
            <div className="py-6 text-center text-xs text-ink-mid">Nothing was delivered beyond the app in the last ninety days.</div>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {channels.map(([channel, counters]) => (
                <li key={channel} className="flex items-center gap-3 text-[11px]">
                  <span className="w-20 text-ink">{CHANNEL_LABELS[channel] ?? channel}</span>
                  <span className="relative h-5 flex-1" role="img" aria-label={`${CHANNEL_LABELS[channel] ?? channel}: ${counters.sent} sent, ${counters.failed} failed`}>
                    <span className="absolute inset-y-0 left-0 rounded" style={{ width: `${(counters.sent / maxSent) * 100}%`, background: MARINE }} />
                    {counters.failed > 0 && <span className="absolute inset-y-0 rounded" style={{ left: `${(counters.sent / maxSent) * 100}%`, width: `${Math.max(1, (counters.failed / maxSent) * 100)}%`, background: BAD }} />}
                  </span>
                  <span className="w-24 text-right text-ink-mid">
                    <span className="font-bold text-ink">{counters.sent.toLocaleString('en-US')}</span>
                    {counters.failed > 0 ? ` · ${counters.failed} failed` : ''}
                    {channel === 'sms' && counters.segments > 0 ? ` · ${counters.segments} parts` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  )
}
